import { useTranslation } from '@framework/i18n';
import { focusFirstAvailableElement, normalizeFieldName } from '@framework/ui';
import { Button, Space } from 'antd';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { LeafError } from '../LeafError';

export const StyledAlertMessageTheme = {
  // variabili antd
  colorError: '#ff4d4f',
};

const Dot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${props =>
    props.theme.colorError ?? StyledAlertMessageTheme.colorError};
`;

const StyledMessageLine = styled.div`
  margin: 0 0 6px 0;
  &:last-of-type {
    margin: 0;
  }
`;

const StyledContainer = styled.div`
  margin-top: 6px;
`;

export interface ErrorsListProps {
  errors?: LeafError[];
}

const ErrorItem = (props: { error: LeafError }) => {
  const { error } = props;
  const { t } = useTranslation();

  const handleFocusElement = useCallback(() => {
    const fieldName = normalizeFieldName(error.path);
    // Proviamo ad ottenere l'elemento del DOM corrispondente al campo
    // di errore su cui effettuare il focus, in ordine:
    // 1. Proviamo con un elemento che specifica l'attributo `data-form-focusable-for`.
    //    Viene usato per effettuare il focus su un elemento specifico _anche se c'è già un input valido_
    // 2. Sull'input con `name=...`
    // 3. Se non lo troviamo, proviamo a cercare l'elemento con l'attributo `data-form-name` (il FormItem).
    const element =
      document.querySelector(`*[data-form-focusable-for="${fieldName}"]`) ??
      document.querySelector(`*[name="${fieldName}"]`) ??
      document.querySelector(`*[data-form-name~="${fieldName}"]`);

    if (!element) {
      console.warn(`[ui/a11y] Non è stato trovato alcun elemento per ${fieldName}`); // prettier-ignore
      return;
    }

    // In caso di elementi disabilitati, eseguiamo comunque lo
    // scroll, in modo da portare l'attenzione sul campo.
    element.scrollIntoView({
      behavior: 'instant', // Così come il focus
      block: 'center',
    });

    focusFirstAvailableElement(element);
  }, [error]);

  return (
    <StyledMessageLine>
      <Space align="center">
        <Dot />
        <div>
          <Button type="text" size="small" onClick={handleFocusElement}>
            {error.error.message}
          </Button>
        </div>
      </Space>
    </StyledMessageLine>
  );
};

/**
 * Lista stilizzata degli errori
 */
export function ErrorsList(props: ErrorsListProps) {
  const { errors } = props;

  // Quando cambiano gli errori, nascondo la lista. Per farlo utilizziamo
  // la `key` per forzare il re-render del componente.
  const errorMessagesKey = errors?.join(',') ?? '';

  if (!errors || errors.length === 0) return null;

  // prendo i primi 3 errori
  const firstErrors = errors.slice(0, 3);
  const moreErrors = errors.slice(3);

  return (
    <StyledContainer>
      {firstErrors.map((error, i) => (
        <ErrorItem key={i} error={error} />
      ))}

      <ToggleErrors key={errorMessagesKey} errors={moreErrors} />
    </StyledContainer>
  );
}

interface ToggleErrorsProps {
  errors: LeafError[];
}

function ToggleErrors(props: ToggleErrorsProps) {
  const { errors } = props;
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState(false);

  const errorCount = errors.length;

  if (errorCount === 0) return null;

  const showMessage =
    errorCount === 1
      ? t('form.error-list.show-more_one', 'Mostra 1 altro errore')
      : t('form.error-list.show-more', 'Mostra altri {{errorCount}} errori', {
          errorCount,
        });
  const hideMessage =
    errorCount === 1
      ? t('form.error-list.hide_one', 'Nascondi 1 errore')
      : t('form.error-list.hide', 'Nascondi {{errorCount}} errori', {
          errorCount,
        });

  return (
    <>
      {showErrors && (
        <div>
          {errors.map((error, i) => (
            <ErrorItem key={i} error={error} />
          ))}
        </div>
      )}
      <Button
        type="link"
        onClick={() => setShowErrors(show => !show)}
        style={{ padding: 0 }}
      >
        {showErrors ? hideMessage : showMessage}
      </Button>
    </>
  );
}
